import { css } from '@emotion/css';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigator } from '../../../routing/navigator';
import { QuestionmarkCircle } from '../../../shared/assets/icons';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { Layout } from '../../../shared/components/layout/Layout';
import { useLocation } from '../../../shared/services/api/location';
import { handlePaymentSubmit } from '../../../shared/services/payment/payment';
import {
  parseAmount,
  hasMaxTwoDecimals,
} from '../../../shared/utils/parseAmount';
import {
  BOTTOM_NAV_SAFE_HEIGHT,
  HEADER_HEIGHT,
} from '../../../shared/styles/css-values';
import {
  doctrineStencilStyle,
  h1Style,
  p2Style,
} from '../../../shared/styles/text';
import { CurrencyAmountSelection } from '../../payment-page/CurrencyAmountSelection';
import { http } from '../../../shared/services/http';
import { useCurrentUser } from '../../../shared/services/api/user';
import { SavedCardSelector } from '../../../shared/components/SavedCardSelector';
import { Loader } from '../../../shared/components/Loader';
import { Colors } from '../../../shared/styles/colors';
import { Radius } from '../../../shared/styles/tokens';

/**
 * Reserve (preauthorize) an amount on the card. The card is only charged for
 * what the sessions actually consume — the rest of the reservation is
 * released automatically when the sessions end.
 */
export const ReserveFundsPage: React.FC = () => {
  const navigator = useNavigator();
  const { locationId } = useParams<{ locationId: string }>();
  const [amount, setAmount] = useState<string>('0');
  const parsedAmount = parseAmount(amount);
  const isAmountValid =
    parsedAmount !== null &&
    parsedAmount > 0 &&
    hasMaxTwoDecimals(parsedAmount);
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
  const [isR1Invoice, setIsR1Invoice] = useState(false);

  const [proceedWaiting, setProceedWaiting] = useState<boolean>(false);
  // Saved cards: null means "a new card", which can then be kept for next time.
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [saveCard, setSaveCard] = useState<boolean>(true);
  const {
    data: user,
    isLoading: userIsLoading,
    isError: userIsError,
  } = useCurrentUser();

  if (!locationId) throw new Error();

  const {
    data: location,
    isLoading,
    isError,
  } = useLocation({ id: locationId });

  if (isError || userIsError) {
    console.error({ isError, userIsError });
    return <p className={errorStyle}>Something went wrong</p>;
  }

  if (isLoading || !location || userIsLoading) {
    return <Loader />;
  }

  return (
    <Layout.Content>
      <div className={innerContainerStyle}>
        <div className={infoContainerStyle}>
          <h1>
            Reserve funds at{' '}
            <span className={doctrineStencilStyle}>{location.name}</span>.
          </h1>
          <p>
            The amount is only reserved on your card. When your sessions end,
            your card is charged for what you actually spent and the rest of the
            reservation is released.
          </p>
        </div>
        <div className={moreInfoStyle}>
          <Button
            startIcon={<QuestionmarkCircle />}
            onClick={() => {
              setShowMoreInfo(!showMoreInfo);
            }}
            text={'More info'}
            size={ButtonSizeEnum.MEDIUM}
            variant={ButtonVariantEnum.TEXT}
          />
          {showMoreInfo && (
            <div>
              <a
                href="/terms-of-use#povrati-sredstava"
                className={policyLinkStyle}
              >
                Payment and refund policy details
              </a>
            </div>
          )}
        </div>
        <CurrencyAmountSelection setAmount={setAmount} />
        <SavedCardSelector
          locationId={location.id}
          selectedCardId={selectedCardId}
          onSelect={setSelectedCardId}
          saveCard={saveCard}
          onSaveCardChange={setSaveCard}
        />
        {user?.r1CompanyAddress &&
        user.r1CompanyName &&
        user.r1VatNumber &&
        user.r1CompanyCountry &&
        user.r1CompanyZipCode &&
        user.r1CompanyCity ? (
          <div className={r1InvoiceDivStyle}>
            <label className={r1InvoiceCheckboxRowStyle}>
              <input
                className={checkboxStyle}
                type="checkbox"
                checked={isR1Invoice}
                onChange={() => setIsR1Invoice(!isR1Invoice)}
              />
              <span>I need company invoice.</span>
            </label>
          </div>
        ) : (
          <div className={r1InvoiceHintStyle}>
            If you need company invoice, please fill the required data in your
            profile.
          </div>
        )}
      </div>
      <div className={buttonActionContainerStyle}>
        <Button
          size={ButtonSizeEnum.LARGE}
          variant={ButtonVariantEnum.TEXT}
          text={'back'}
          onClick={navigator.toPrevious}
        />
        <Button
          size={ButtonSizeEnum.LARGE}
          variant={ButtonVariantEnum.FILLED}
          text={'reserve on card'}
          disabled={!isAmountValid || proceedWaiting}
          loading={proceedWaiting}
          onClick={async () => {
            if (!proceedWaiting) {
              setProceedWaiting(true);
              if (user) {
                const account = user.accounts?.find(
                  (a) =>
                    a.location.id === location.id ||
                    a.location.subLocations?.some(
                      (sl) => sl.id === location.id,
                    ),
                );
                if (!account) {
                  await http.createAccount(location.id);
                }
              }
              await handlePaymentSubmit(
                amount,
                location.id,
                isR1Invoice,
                'PREAUTHORIZATION',
                { saveCard, savedCardId: selectedCardId },
              );
              setProceedWaiting(false);
            }
          }}
        />
      </div>
    </Layout.Content>
  );
};

const innerContainerStyle = css`
  display: flex;
  flex-flow: column;
  align-items: start;
  gap: 1em;
  margin-top: calc(6.375rem - ${HEADER_HEIGHT});
  min-height: calc(
    100vh - 6.375rem - ${HEADER_HEIGHT} - ${BOTTOM_NAV_SAFE_HEIGHT}
  );
  padding-bottom: 1rem;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const infoContainerStyle = css`
  h1 {
    ${h1Style}
    overflow-wrap: anywhere;
  }
  p {
    ${p2Style}
    color: ${Colors.TEXT_SECONDARY};
    margin-top: 1rem;
  }
`;

const buttonActionContainerStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  width: calc(100% + 3rem);
  justify-content: space-between;
  gap: 1rem;
  margin: 0 -1.5rem -2rem;
  padding: 0.75rem 1.5rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.94);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  backdrop-filter: saturate(160%) blur(14px);
  border-top: 1px solid ${Colors.BORDER};
`;

const moreInfoStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const policyLinkStyle = css`
  ${p2Style}
  color: ${Colors.CAPRI_BLUE};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
`;

const r1InvoiceDivStyle = css`
  ${p2Style}
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
`;

const r1InvoiceHintStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const r1InvoiceCheckboxRowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

const checkboxStyle = css`
  width: 20px;
  height: 20px;
  margin: 0;
  flex: 0 0 auto;
  cursor: pointer;
  accent-color: ${Colors.CAPRI_BLUE};
`;
